import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import Tickets from '@/pages/Tickets';
import Documents from '@/pages/Documents';
import Academy from '@/pages/Academy';
import Suggestions from '@/pages/Suggestions';
import Refer from '@/pages/Refer';
import Support from '@/pages/Support';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { factsFromRows } from '@/lib/studioFacts';
import { useReferralProgramme } from '@/lib/useReferralProgramme';
import { MyTicketsSection } from '@/pages/MyInfo';
import { WingTabs } from '@/components/WingTabs';
import { isCell } from '@/lib/cell/config';
import { cellGet } from '@/lib/cell/api';

/**
 * MY STUDIO — the facility wing (Direction A of the Two Wings IA; Peter,
 * 2026-08-27: "A works, let's go with A then C" — decision aa4fc89d, mockup
 * dev.cadenceadvisers.com/design/pro-hub-ia.html).
 *
 * Everything about the BUILDING and the pro's life inside it lives here, one
 * tap below the bar: maintenance, the door + Wi-Fi codes, documents, the
 * Academy, referrals, the suggestion box, the community channel. Me (/my-info)
 * keeps identity only — profile, license, insurance, emergency contact.
 *
 * The Wi-Fi + access-code cards MOVED here from My Info in the same change
 * that renamed it Me: a door code is a fact about the studio, not about the
 * person, and My Info gets opened next to clients (feedback 0f346ad2 — the
 * masking behaviour travels with the card, pinned by MyStudio.test.tsx).
 */

type Suite = { id: string; suite_number: string | number; square_feet: number | null };

type StudioData = {
  proId: string | null;
  /** True when no pro/studio resolved — a failed view-as or an unlinked account. The wing
   *  must SAY so: a silently empty Access tab reads as "my code does not show" (Peter,
   *  2026-08-29, the day the codes were entered). */
  unresolved?: boolean;
  suite: Suite | null;
  accessCode: string | null;
  displayName: string;
  address: string;
  wifi: {
    pro?: { ssid: string; password: string };
    guest?: { ssid: string; password: string };
  };
  slackUrl: string | null;
  openTickets: number;
  /** HUB-074: reads that failed — a door code or Wi-Fi that could not be READ is never shown as one that does not exist. */
  unread?: string[];
};

type CellStudio = {
  pro: { id: string } | null;
  tenant: { id: string; suite_id: string | null; access_code: string | null; hub_access_code: string | null } | null;
  suite: Suite | null;
  kb: Array<{ key: string; value: string | null }> | null;
  openTickets: number | null;
};

/** The same StudioData the queryFn builds, from ONE call on the AWS cell (move map M28). A failed call is named
 *  unread (HUB-074), never an empty studio. */
async function cellMyStudio(userId: string, activeLocationId: string | null, empty: StudioData): Promise<StudioData> {
  const path = activeLocationId
    ? `/v1/pro-hub/locations/${encodeURIComponent(activeLocationId)}/users/${userId}/my-studio`
    : `/v1/pro-hub/users/${userId}/my-studio`;
  // CELL (M28): GET /v1/pro-hub/(locations/{locationId}/)users/{authUserId}/my-studio — pros, tenants, suites, kb, tickets.
  const { data, error } = await cellGet<CellStudio>(path);
  if (error || !data) return { ...empty, unread: ['your profile'] };
  const { pro, tenant } = data;
  if (!pro || !activeLocationId) return empty;
  const f = factsFromRows(data.kb ?? []);
  return {
    proId: pro.id,
    suite: data.suite ?? null,
    accessCode: tenant?.access_code ?? tenant?.hub_access_code ?? null,
    displayName: f.displayName ?? '',
    address: f.address ?? '',
    wifi: {
      pro: f.raw.wifi_pro_ssid
        ? { ssid: f.raw.wifi_pro_ssid, password: f.raw.wifi_pro_pw ?? '' } : undefined,
      guest: f.raw.wifi_guest_ssid
        ? { ssid: f.raw.wifi_guest_ssid, password: f.raw.wifi_guest_pw ?? '' } : undefined,
    },
    slackUrl: f.communityEnabled ? f.communityUrl : null,
    openTickets: data.openTickets ?? 0,
    unread: [],
  };
}

export default function MyStudio() {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get('tab') ?? 'access';
  const tab = ['repairs', 'docs', 'academy', 'ideas', 'refer', 'support'].includes(rawTab) ? rawTab : 'access';
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();
  const referral = useReferralProgramme();

  const { data, isLoading, refetch } = useQuery<StudioData>({
    queryKey: ['my-studio', userId, activeLocationId],
    enabled: !!userId,
    queryFn: async () => {
      const empty: StudioData = {
        proId: null, suite: null, accessCode: null, displayName: '',
        address: '', wifi: {}, slackUrl: null, openTickets: 0, unresolved: true,
      };
      // CELL (M28): the whole load is one endpoint on the AWS cell (cellMyStudio below); Supabase below is untouched.
      if (isCell()) return cellMyStudio(userId!, activeLocationId ?? null, empty);
      const { data: pro, error: proErr } = await supabase
        .from('pros')
        .select('id')
        .eq('auth_user_id', userId!)
        .maybeSingle();
      if (proErr) return { ...empty, unread: ['your profile'] };
      if (!pro || !activeLocationId) return empty;
      const unread: string[] = [];

      // Tenant row at the active location — same scoping as My Info's read.
      // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
      const { data: tenant, error: tenantErr } = await supabase
        .from('tenants')
        .select('id, suite_id, access_code, hub_access_code')
        .eq('pro_id', pro.id)
        .eq('location_id', activeLocationId)
        .maybeSingle<{ id: string; suite_id: string | null; access_code: string | null; hub_access_code: string | null }>();
      if (tenantErr) unread.push('your suite and door code');

      let suite: Suite | null = null;
      if (tenant?.suite_id) {
        // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
        const { data: s, error: suiteErr } = await supabase
          .from('suites')
          .select('id, suite_number, square_feet')
          .eq('id', tenant.suite_id)
          .maybeSingle<Suite>();
        if (suiteErr) unread.push('your suite');
        suite = s ?? null;
      }

      // MY STUDIO IS THE SOURCE (Peter, 2026-08-26) — the owner's own words in
      // location_kb_values, never location_configs. See lib/studioFacts.ts.
      // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
      const { data: kb, error: kbErr } = await supabase
        .from('location_kb_values')
        .select('key, value')
        .eq('location_id', activeLocationId)
        .eq('ns', 'location');
      if (kbErr) unread.push('the studio’s Wi-Fi and details');
      const f = factsFromRows(kb ?? []);

      // CELL (M28): production only — the cell path is the isCell() branch above, which returns first.
      const { count, error: countErr } = await supabase
        .from('tickets')
        .select('id', { count: 'exact', head: true })
        .eq('pro_id', pro.id)
        .eq('location_id', activeLocationId)
        .in('status', ['open', 'pending', 'in_progress', 'on_hold', 'scheduled']);

      return {
        proId: pro.id,
        suite,
        // access_code (canonical, building access) → hub_access_code (legacy).
        accessCode: tenant?.access_code ?? tenant?.hub_access_code ?? null,
        displayName: f.displayName ?? '',
        address: f.address ?? '',
        wifi: {
          pro: f.raw.wifi_pro_ssid
            ? { ssid: f.raw.wifi_pro_ssid, password: f.raw.wifi_pro_pw ?? '' } : undefined,
          guest: f.raw.wifi_guest_ssid
            ? { ssid: f.raw.wifi_guest_ssid, password: f.raw.wifi_guest_pw ?? '' } : undefined,
        },
        // The community channel is gated on the owner's own toggle — an unset
        // channel offers no dead row.
        slackUrl: f.communityEnabled ? f.communityUrl : null,
        openTickets: count ?? 0,
        unread: countErr ? [...unread, 'your maintenance requests'] : unread,
      };
    },
  });

  const [accessOpen, setAccessOpen] = useState(false);

  if (isLoading || !data) {
    return <div className="text-image-slate text-sm">Loading…</div>;
  }

  const hasAccessSection = !!(data.suite || data.accessCode || data.wifi.pro?.ssid || data.wifi.guest?.ssid);
  void hasAccessSection;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold text-image-black">
          <span className="text-image-pink">Studio</span>
        </h1>
        <p className="text-sm text-image-charcoal mt-1">
          {data.displayName
            ? `Everything for your day at ${data.displayName}.`
            : 'Everything for your day at the studio.'}
        </p>
      </div>

      {(data.unread?.length ?? 0) > 0 && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="studio-unread">
          <span>We couldn&rsquo;t load {data.unread!.join(', ')} just now — this is a connection problem, not missing information.</span>
          <button type="button" className="underline shrink-0" onClick={() => { void refetch(); }}>Try again</button>
        </div>
      )}

      {/* THE TABS (Peter, on the view-as walk 2026-08-29: "It should be more like Grow
          where the tabs are on the top. Right now... you click on a thing and the entire
          thing goes away. If you're in Grow, the navigation works because you stay in
          Grow"). Short labels so they fit across a phone — his call. Each pane mounts the
          REAL page component, one implementation each; the old standalone addresses
          redirect here so every existing link keeps working:
            /tickets -> ?tab=repairs   /documents -> ?tab=docs   /academy -> ?tab=academy
            /suggestions -> ?tab=ideas /refer -> ?tab=refer      (Support stays a public
          page too — its tab mounts the same component.) */}
      {/* The shared wing row (PROHUB-43) — seven pills at the old size wrapped into a
          ragged pile with an orphan; the compact shared treatment packs them evenly. */}
      <div data-studio-tabs>
        <WingTabs
          tabs={[
            ['access', 'Access'],
            ['repairs', 'Repairs'],
            ['docs', 'Docs'],
            ['academy', 'Academy'],
            ['ideas', 'Ideas'],
            ...(referral.enabled ? [['refer', 'Refer'] as const] : []),
            ['support', 'Support'],
          ] as ReadonlyArray<readonly [string, string]>}
          active={tab}
          onPick={(k) => setParams(k === 'access' ? {} : { tab: k }, { replace: true })}
        />
      </div>

      {tab === 'access' && (
        <>
          {data.unresolved && (
            <p className="text-sm text-image-charcoal" data-studio-unresolved>
              This studio view could not load. If you are viewing as a pro, this account may
              not be able to see that pro&rsquo;s studio — exit the view and check which
              account is signed in (top right).
            </p>
          )}
          <div className="bg-white ring-1 ring-image-border rounded-2xl divide-y divide-image-border overflow-hidden">
        {hasAccessSection && (
          <div>
            <button
              type="button"
              onClick={() => setAccessOpen(v => !v)}
              aria-expanded={accessOpen}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-image-fog transition-colors"
            >
              <span className="w-9 h-9 rounded-xl bg-image-fog text-image-pink flex items-center justify-center shrink-0"><WifiIcon /></span>
              <span className="min-w-0 flex-1">
                <span className="block font-bold text-sm text-image-black">Wi-Fi &amp; door access</span>
                <span className="block text-xs text-image-charcoal">Codes, tap to reveal</span>
              </span>
              <span className="text-xs font-bold text-image-pink">{accessOpen ? 'Hide' : 'Show'}</span>
            </button>
            {accessOpen && (
              <div className="px-4 pb-4 space-y-3">
                {data.suite && (
                  <div className="rounded-xl bg-image-fog px-3 py-2.5 text-xs space-y-1.5">
                    <FactRow label="Suite" value={String(data.suite.suite_number)} />
                    {data.suite.square_feet && <FactRow label="Sq Ft" value={`${data.suite.square_feet}`} />}
                    {data.address && <FactRow label="Address" value={data.address} />}
                  </div>
                )}
                {data.accessCode && <AccessCodeCard code={data.accessCode} />}
                {(data.wifi.pro?.ssid || data.wifi.guest?.ssid) && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {data.wifi.pro?.ssid && (
                      <WifiRow label="Pro" ssid={data.wifi.pro.ssid} password={data.wifi.pro.password} />
                    )}
                    {data.wifi.guest?.ssid && (
                      <WifiRow label="Guest" ssid={data.wifi.guest.ssid} password={data.wifi.guest.password} />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {data.slackUrl && (
          <WingRow href={data.slackUrl} icon={<ChatIcon />} label="Slack" sub="Your studio community" />
        )}
          </div>
        </>
      )}

      {tab === 'repairs' && (
        <>
          <Tickets />
          {data.proId && <MyTicketsSection proId={data.proId} />}
        </>
      )}
      {tab === 'docs' && <Documents />}
      {tab === 'academy' && <Academy />}
      {tab === 'ideas' && <Suggestions />}
      {tab === 'refer' && referral.enabled && <Refer />}
      {tab === 'support' && <Support />}
    </div>
  );
}

function WingRow({ to, href, icon, label, sub, badge }: {
  to?: string; href?: string; icon: React.ReactNode; label: string; sub: string; badge?: boolean;
}) {
  const inner = (
    <>
      <span className="w-9 h-9 rounded-xl bg-image-fog text-image-pink flex items-center justify-center shrink-0 relative">
        {icon}
        {badge && <span aria-label="has updates" className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full bg-image-pink ring-2 ring-white" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-bold text-sm text-image-black">{label}</span>
        <span className="block text-xs text-image-charcoal">{sub}</span>
      </span>
    </>
  );
  const cls = 'w-full flex items-center gap-3 px-4 py-3.5 hover:bg-image-fog transition-colors';
  if (href) {
    return <a href={href} target="_blank" rel="noreferrer" className={cls}>{inner}</a>;
  }
  return <Link to={to!} className={cls}>{inner}</Link>;
}

function FactRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-image-slate">{label}</span>
      {/* break-words, never break-all — break-all split a postcode mid-number
          on a pro's real screen (2026-08-09). */}
      <span className="text-image-black text-right break-words">{value}</span>
    </div>
  );
}

/** MOVED from MyInfo.tsx with the wing (feedback 0f346ad2, Phil: "Why is the
 *  access code exposed?"): the pro's own door + alarm code, MASKED by default,
 *  tap-to-reveal, re-hides on its own — a walked-away phone must never be
 *  showing the building code. */
function AccessCodeCard({ code }: { code: string }) {
  const [shown, setShown] = useState(false);
  // Auto-rehide: a revealed code must not outlive the glance.
  useEffect(() => {
    if (!shown) return;
    const t = setTimeout(() => setShown(false), 8000);
    return () => clearTimeout(t);
  }, [shown]);
  return (
    <div className="rounded-2xl bg-image-black text-white p-4 ring-1 ring-image-pink/20">
      <div className="flex items-center gap-1.5">
        <span className="text-image-pink"><LockIcon /></span>
        <h3 className="ph-sec !text-white">Access Code</h3>
      </div>
      <button
        type="button"
        onClick={() => setShown(v => !v)}
        aria-label={shown ? 'Hide access code' : 'Show access code'}
        className="font-display text-3xl tracking-[0.35em] text-image-pink font-bold mt-2 select-none"
      >
        {shown ? code : '•'.repeat(Math.max(code.length, 4))}
      </button>
      <p className="text-[11px] text-white/50 mt-1.5">
        Door keypad &amp; alarm. Tap to {shown ? 'hide' : 'reveal'} — hides itself in a few seconds. Do not share.
      </p>
    </div>
  );
}

/** MOVED from MyInfo.tsx with the wing. */
function WifiRow({ label, ssid, password }: { label: string; ssid: string; password?: string }) {
  return (
    <div className="rounded-lg bg-image-fog px-3 py-2 flex items-center justify-between gap-2">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-image-slate">{label}</div>
        <div className="text-image-black font-medium text-sm truncate">{ssid}</div>
      </div>
      {password && (
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-wider text-image-slate">Pass</div>
          <div className="font-mono text-image-black text-xs">{password}</div>
        </div>
      )}
    </div>
  );
}

/** ---- Inline SVG icons ---- */
const ip = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
function ChatIcon() { return (<svg {...ip}><path d="M3 12a9 9 0 1 1 4.5 7.8L3 21l1.2-4.5A8.96 8.96 0 0 1 3 12z" /></svg>); }
function WifiIcon() { return (<svg {...ip}><path d="M5 12.5a10 10 0 0 1 14 0" /><path d="M8.5 15.5a5 5 0 0 1 7 0" /><path d="M12 19h.01" /></svg>); }
function LockIcon() { return (<svg {...ip}><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>); }
